/**
 * Kickoff spawns — the five standard positions per team, who gets which one,
 * and the state reset that putting a car on a spawn implies.
 *
 * The POSITIONS are data and come from ./../physics/arena-data (RocketSim's
 * `CAR_SPAWN_LOCATIONS_SOCCAR`, cross-checked against the RLBot wiki). Nothing
 * here re-derives them.
 *
 * The ASSIGNMENT — which of the five a given player takes for a given roster
 * size — is a convention, not a sourced constant. The real game picks it from a
 * per-match shuffle we cannot reproduce and would not want to: this simulation
 * has to be reproducible from an input log alone, so the mapping below is
 * fixed, documented, and identical on every run. It matches the arrangement
 * players actually see:
 *
 *   1v1  far back centre
 *   2v2  both corners
 *   3v3  both corners + far back centre
 *   4v4  both corners + both back positions
 *   5v5  all five
 *
 * Rosters larger than five wrap, so no roster size can throw.
 *
 * A "spawn" also implies a state reset. `resetCarToSpawn` is the kickoff twin
 * of `respawnCar` in ../physics/demo: same fields, different height and boost
 * rules (kickoff puts the car ON the ground at CAR_SPAWN_REST_Z; a demo respawn
 * drops it in from CAR_RESPAWN_Z). The two are deliberately separate functions
 * rather than one parameterized one, because the demo respawn is transcribed
 * from RocketSim and this one is not.
 */

import { KICKOFF_SPAWNS, type Spawn } from '../physics/arena-data';
import { resetBoostBurst } from '../physics/boost';
import type { CarState } from '../physics/car-state';
import { BOOST_SPAWN_AMOUNT } from '../physics/constants';
import { resetGroundController } from '../physics/ground';
import { setWasJumpDown } from '../physics/jump';
import { clone, eulerToRotation, vec3 } from '../physics/math';

/** Number of standard kickoff positions per team. */
export const KICKOFF_POSITION_COUNT = KICKOFF_SPAWNS[0].length;

/**
 * Index of each named position inside `KICKOFF_SPAWNS[team]`, so callers never
 * have to remember that "far back centre" is 4.
 */
export const KICKOFF_POSITION = {
  rightCorner: 0,
  leftCorner: 1,
  backRight: 2,
  backLeft: 3,
  farBackCentre: 4,
} as const;

/**
 * Which spawn indices a roster of `n` players uses, for n = 0..5. Indexed by
 * team size; see the header for why this is a convention rather than a source.
 */
export const KICKOFF_FORMATIONS: readonly (readonly number[])[] = [
  [],
  [KICKOFF_POSITION.farBackCentre],
  [KICKOFF_POSITION.rightCorner, KICKOFF_POSITION.leftCorner],
  [KICKOFF_POSITION.rightCorner, KICKOFF_POSITION.leftCorner, KICKOFF_POSITION.farBackCentre],
  [
    KICKOFF_POSITION.rightCorner,
    KICKOFF_POSITION.leftCorner,
    KICKOFF_POSITION.backRight,
    KICKOFF_POSITION.backLeft,
  ],
  [0, 1, 2, 3, 4],
];

/**
 * The spawn indices used by a team of `teamSize` players, in team order.
 * Sizes above 5 repeat the five positions in order, so two cars share a spawn
 * rather than the lookup failing — cars overlapping at kickoff is a caller
 * problem, not something this module can fix.
 */
export const kickoffFormation = (teamSize: number): readonly number[] => {
  const size = Math.max(0, Math.floor(teamSize));
  const table = KICKOFF_FORMATIONS[size];
  if (table) return table;
  const out: number[] = [];
  for (let i = 0; i < size; i++) out.push(i % KICKOFF_POSITION_COUNT);
  return out;
};

/** The spawn a team's `indexInTeam`-th player takes, for the given roster size. */
export const kickoffSpawn = (team: 0 | 1, indexInTeam: number, teamSize: number): Spawn => {
  const formation = kickoffFormation(teamSize);
  const n = formation.length;
  const slot = n === 0 ? 0 : formation[((indexInTeam % n) + n) % n];
  const spawns = KICKOFF_SPAWNS[team];
  return spawns[slot % spawns.length];
};

/**
 * Put a car on a kickoff spawn and clear everything that could survive from the
 * previous play: velocity, spin, the jump/dodge machine, the demolition flags
 * and the three pieces of controller memory the car systems own (the powerslide
 * ramp, the boost burst, and the jump button's previous level). Those three
 * lived in module-level WeakMaps until CarState grew fields for them; missing
 * any one of them is what makes a "reset" simulation diverge from a fresh one,
 * so they are cleared here rather than left to the caller.
 */
export const resetCarToSpawn = (
  car: CarState,
  spawn: Spawn,
  boostAmount: number = BOOST_SPAWN_AMOUNT
): void => {
  car.position = clone(spawn.position);
  car.velocity = vec3(0, 0, 0);
  car.angularVelocity = vec3(0, 0, 0);
  car.orientation = eulerToRotation(0, spawn.yaw, 0);

  // Kickoff spawns are AT the resting height, so the car starts grounded — the
  // opposite of a demolition respawn, which drops in from above.
  car.surface = 'floor';
  car.surfaceNormal = vec3(0, 0, 1);
  car.onGround = true;

  car.boostAmount = boostAmount;
  car.supersonic = false;
  car.supersonicTimer = 0;

  car.hasJumped = false;
  car.hasFlip = true;
  car.isDodging = false;
  car.jumpHoldTime = 0;
  car.airTime = 0;
  car.dodgeTime = 0;
  car.dodgeDirection = vec3(0, 0, 0);
  car.dodgeCooldown = 0;

  car.demolished = false;
  car.respawnTimer = 0;

  resetGroundController(car);
  resetBoostBurst(car);
  setWasJumpDown(car, false);
};

/**
 * Kickoff placement for one match configuration.
 *
 * Holds the roster only; it has no per-tick state and no memory of previous
 * kickoffs, so calling `place` twice with the same cars is idempotent. That is
 * what lets the world hold the field frozen during the countdown by simply
 * re-placing every tick.
 */
export class Kickoff {
  constructor(readonly teamSizes: readonly [number, number]) {}

  /** Roster size derived from an actual car list, so the two cannot drift. */
  static forCars(cars: readonly CarState[]): Kickoff {
    let blue = 0;
    let orange = 0;
    for (const car of cars) {
      if (car.team === 0) blue++;
      else orange++;
    }
    return new Kickoff([blue, orange]);
  }

  /** Spawn for the `indexInTeam`-th car of `team`. */
  spawnFor(team: 0 | 1, indexInTeam: number): Spawn {
    return kickoffSpawn(team, indexInTeam, this.teamSizes[team]);
  }

  /**
   * Place every car on its spawn, in team order. Cars are matched to spawns by
   * their order WITHIN their team in `cars`, so the assignment depends only on
   * the car list and never on iteration order elsewhere.
   */
  place(cars: readonly CarState[], boostAmount: number = BOOST_SPAWN_AMOUNT): void {
    const seen: [number, number] = [0, 0];
    for (const car of cars) {
      const indexInTeam = seen[car.team]++;
      resetCarToSpawn(car, this.spawnFor(car.team, indexInTeam), boostAmount);
    }
  }
}
